import { useEffect, useRef } from 'react';
import {
  Modal,
  ChatPanel,
  MessageBubble,
  Composer,
  IconButton,
  Icon,
  icons,
  Button,
  Callout,
  theme,
  type Message,
} from '@agentisai/design-system';
import { AttachFileButton } from './AttachFileButton';
import { AttachmentChips } from './AttachmentChips';
import { composerChrome } from './composerChrome.css';
import { buttonRow, dialog as dialogCls, dialogChatPanel } from './CreateWorkstreamPanel.css';

const CW_AGENT_INTRO: Message = {
  id: 'cw-agent-1',
  memberId: 'workstream-agent',
  memberName: 'Discovery',
  memberRole: '',
  memberType: 'agent',
  variant: 'mira',
  text: "Let's set up your workstream — in a sentence, what outcome are you driving?",
  time: 'Just now',
};

export interface CreateWorkstreamPanelProps {
  open: boolean;
  onClose: () => void;
  /** Conversation transcript (DS Message objects). Rendered after the seeded intro. */
  messages?: Message[];
  /**
   * Preformatted display time for the seeded agent intro bubble (the parent
   * stamps it at panel-open and formats it through the same formatter as the
   * transcript, so it ages consistently). Defaults to `'Just now'`.
   */
  introTime?: string;
  value?: string;
  onChange?: (value: string) => void;
  onSend?: (value: string) => void;
  sendDisabled?: boolean;
  /**
   * Max characters the composer value may have (the shared `MAX_TEXT_LEN`
   * contract limit). When `value` exceeds it, an inline over-limit message shows
   * — so the user gets feedback BEFORE sending, not a post-send server rejection.
   * The parent drives `sendDisabled` from the same check. Omit to skip it.
   */
  maxLength?: number;
  /** In-flight chat-turn indicator — disables send while true. */
  pending?: boolean;
  onCreate?: () => void;
  createDisabled?: boolean;
  creating?: boolean;
  /** Error message from the send-chat-turn mutation (rendered as a live region). */
  sendError?: string | null;
  /** Error message from the create-workstream mutation (rendered as a live region). */
  createError?: string | null;
  /**
   * Files staged for this workstream-to-be. They cannot upload yet — the upload
   * routes are gated by `resolveOwned`, which 404s until the workstream row
   * exists — so the parent holds them and flushes them the moment auto-create
   * (ADR-0044) mints the id, after which they appear in the Files rail.
   */
  stagedFiles?: readonly File[];
  /** Files were picked. Validation + staging are the parent's job. */
  onFilesPicked?: (files: File[]) => void;
  /** Drop one staged file. */
  onRemoveFile?: (file: File) => void;
  /** Name of the file uploading during the post-create flush, or `null`. */
  uploadingFilename?: string | null;
  /** Upload progress 0–100 for {@link uploadingFilename}. */
  uploadProgress?: number;
  /** Attach validation / upload failure copy, already user-facing. */
  attachError?: string | null;
}

/**
 * CreateWorkstreamPanel — one-off page composition for the "Create new
 * workstream" surface. Composes DS Modal (focus trap, Escape, backdrop
 * dismiss) + ChatPanel fluid + MessageBubble (seeded agent intro + transcript)
 * + Composer (empty-tenant placeholder) + Create affordance.
 *
 * State is fully owned by the parent (HomePage). This component renders only.
 * S16 drives the conversation props via two useMutations in HomePage.
 */
export function CreateWorkstreamPanel({
  open,
  onClose,
  messages = [],
  introTime = 'Just now',
  value,
  onChange,
  onSend,
  sendDisabled,
  maxLength,
  pending = false,
  onCreate,
  createDisabled = true,
  creating = false,
  sendError,
  createError,
  stagedFiles = [],
  onFilesPicked,
  onRemoveFile,
  uploadingFilename = null,
  uploadProgress = 0,
  attachError,
}: CreateWorkstreamPanelProps) {
  // Auto-scroll the message list to the bottom whenever a new chat turn lands
  // (the server reply, the user echo, or the create confirmation) — or when
  // the awaiting-reply typing indicator appears — so the latest content is
  // visible. A sentinel at the end of the messages slot locates the
  // ChatPanel's `overflow-y:auto` message list, which is then pinned to its
  // full scroll height (past the list's bottom padding + the typing-indicator
  // lozenge, so the newest item sits flush at the bottom). Imperative DOM
  // sync — the sanctioned use of an effect + ref (frontend-architecture
  // R24/R25), not data-in-effect.
  const bottomRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    let el = bottomRef.current?.parentElement ?? null;
    while (el && el.scrollHeight <= el.clientHeight) el = el.parentElement;
    if (el) el.scrollTop = el.scrollHeight;
  }, [open, messages.length, pending]);

  // The paperclip stages files rather than uploading them: there is no
  // workstream row to own an artifact yet (the pre-create draft seam, ADR-0047),
  // so the parent holds the picked Files and flushes them into the ledger the
  // instant auto-create mints the id. Nothing is claimed that cannot be
  // delivered — the X removes a file nothing has stored.
  const composerLeadingActions = (
    <>
      {onFilesPicked && (
        <AttachFileButton onFilesPicked={onFilesPicked} disabled={uploadingFilename !== null} />
      )}
      <IconButton
        aria-label="Quick actions"
        size="sm"
        variant="ghost"
        icon={<Icon as={icons.Zap} size={14} />}
      />
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create New Workstream"
      size="lg"
      flushBody
      className={dialogCls}
    >
      <ChatPanel
        fluid
        className={dialogChatPanel}
        awaitingReply={pending}
        // The pre-create turn is `forcedAgents: ['discovery']` in the BFF, so
        // the answering agent is known here — no reason to say "Workstream
        // Agent is typing" when discovery is the only agent that can reply.
        typingLabel="Discovery is typing"
        messages={
          <>
            <MessageBubble
              key={CW_AGENT_INTRO.id}
              message={{ ...CW_AGENT_INTRO, time: introTime }}
            />
            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}
            {/* Scroll anchor — kept in view as new turns arrive. */}
            <div ref={bottomRef} aria-hidden="true" />
          </>
        }
        composer={
          <>
            {/* Same band, same rhythm as the workstream conversation. Rendered
                only when it has content: unlike there, every block here is
                conditional, so an always-present wrapper would leave a stray gap
                above the Composer on the common empty case. */}
            {(sendError || stagedFiles.length > 0) && (
              <div className={composerChrome}>
                {sendError && (
                  <div role="alert" aria-live="assertive" style={{ paddingInline: theme.space[4] }}>
                    <Callout tone="danger">{sendError}</Callout>
                  </div>
                )}
                {onRemoveFile && (
                  <AttachmentChips
                    files={stagedFiles}
                    onRemove={onRemoveFile}
                    uploadingName={uploadingFilename}
                    uploadProgress={uploadProgress}
                  />
                )}
              </div>
            )}
            <Composer
              value={value}
              onChange={onChange}
              onSend={onSend}
              sendDisabled={sendDisabled}
              prominence="spotlight"
              placeholder="Click here to begin…"
              leadingActions={composerLeadingActions}
            />
            {maxLength != null && (value?.length ?? 0) > maxLength && (
              <div
                role="alert"
                aria-live="assertive"
                style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
              >
                <Callout tone="danger">
                  Message is too long — {(value?.length ?? 0).toLocaleString()} of{' '}
                  {maxLength.toLocaleString()} characters. Shorten it to send.
                </Callout>
              </div>
            )}
            {attachError && (
              <div
                role="alert"
                aria-live="assertive"
                style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
              >
                <Callout tone="danger">{attachError}</Callout>
              </div>
            )}
            {createError && (
              <div
                role="alert"
                aria-live="assertive"
                style={{ padding: `${theme.space[2]} ${theme.space[4]} 0` }}
              >
                <Callout tone="danger">{createError}</Callout>
              </div>
            )}
            <div className={buttonRow}>
              <Button variant="primary" disabled={createDisabled || creating} onClick={onCreate}>
                {creating ? 'Creating…' : 'Create workstream'}
              </Button>
            </div>
          </>
        }
      />
    </Modal>
  );
}
CreateWorkstreamPanel.displayName = 'CreateWorkstreamPanel';
